import React, { useContext, useEffect, useState, useCallback } from 'react'
import styles from './styles.module.css'
import { useDispatch, useSelector } from 'react-redux'
import ModalFooter from '../../../components/ModalFooter/ModalFooter'
import { EducationalStructureContext } from '../../../utils/context'
import {
  getBasicEduGroup, getBasicEduCourses, saveBasicEduLinks, checkCoursesByClasses,
  getDopEduGroups, getDopEduCourses, saveDopEduLinks, checkCoursesByDopGroups,
  getOutEduGroups, getOutEduCourses, saveOutEduLinks, checkCoursesByOutGroups,
  getElectiveGroups, saveElectiveLinks,
} from '../../../actions/educationalStructure'

const TAB_CONFIG = {
  basicEducation: {
    groupsTitle: 'Курирует классы',
    coursesTitle: 'Курирует предметы',
    hasClosed: true,
    hasAutoCheck: true,
    autoCheckLabel: 'Отметить для классов',
    singleList: false,
    getGroups: (id) => getBasicEduGroup(id),
    getCourses: (id, withClosed) => getBasicEduCourses(id, withClosed),
    saveLinks: (did, gids, cids) => saveBasicEduLinks(did, gids, cids),
    autoCheck: (did, gids) => checkCoursesByClasses(did, gids),
    groupsKey: 'basicGroups',
    coursesKey: 'basicCourses',
  },
  additionalEducation: {
    groupsTitle: 'Курирует группы',
    coursesTitle: 'Курирует предметы',
    hasClosed: false,
    hasAutoCheck: true,
    autoCheckLabel: 'Отметить для групп',
    singleList: false,
    getGroups: (id) => getDopEduGroups(id),
    getCourses: (id) => getDopEduCourses(id),
    saveLinks: (did, gids, cids) => saveDopEduLinks(did, gids, cids),
    autoCheck: (did, gids) => checkCoursesByDopGroups(did, gids),
    groupsKey: 'dopGroups',
    coursesKey: 'dopCourses',
  },
  extracurricularActivities: {
    groupsTitle: 'Курирует группы',
    coursesTitle: 'Курирует предметы',
    hasClosed: false,
    hasAutoCheck: true,
    autoCheckLabel: 'Отметить для групп',
    singleList: false,
    getGroups: (id) => getOutEduGroups(id),
    getCourses: (id) => getOutEduCourses(id),
    saveLinks: (did, gids, cids) => saveOutEduLinks(did, gids, cids),
    autoCheck: (did, gids) => checkCoursesByOutGroups(did, gids),
    groupsKey: 'outGroups',
    coursesKey: 'outCourses',
  },
  electives: {
    groupsTitle: 'Курирует элективные группы',
    coursesTitle: null,
    hasClosed: false,
    hasAutoCheck: false,
    singleList: true,
    getGroups: (id) => getElectiveGroups(id),
    getCourses: null,
    saveLinks: (did, gids, allElectiveGids) => saveElectiveLinks(did, gids, allElectiveGids),
    autoCheck: null,
    groupsKey: 'electiveGroups',
    coursesKey: null,
  },
}

// Получить идентификатор из записи
const getItemId = (item) => item?.gid ?? item?.xp_key ?? item?.cid ?? item?.id

// Получить имя из записи
const getItemName = (item) => item?.name ?? item?.title ?? ''

// Инициализировать отмеченные из данных (ok > 0)
const initChecked = (items) => new Set(
  (items || []).filter(it => it.ok > 0 || it.checked).map(getItemId).filter(Boolean)
)

function CheckList({ title, items, checked, onChange, onSelectAll, footer, emptyHint }) {
  const allChecked = items?.length > 0 && items.every(it => checked.has(getItemId(it)))

  return (
    <div className={styles.checkList}>
      <div className={styles.checkListHeader}>{title}</div>
      <div className={styles.checkListItems}>
        {items?.length
          ? items.map(item => {
              const id = getItemId(item)
              return (
                <label key={id} className={styles.checkItem}>
                  <input
                    type='checkbox'
                    checked={checked.has(id)}
                    onChange={() => onChange(id)}
                  />
                  <span>{getItemName(item)}</span>
                </label>
              )
            })
          : (emptyHint && <div className={styles.checkListEmptyHint}>{emptyHint}</div>)
        }
      </div>
      <div className={styles.checkListFoot}>
        <label className={styles.checkItem}>
          <input
            type='checkbox'
            checked={allChecked}
            onChange={(e) => onSelectAll(e.target.checked)}
          />
          <span>Выделить все</span>
        </label>
        {footer}
      </div>
    </div>
  )
}

export default function BaseEducationTab({ tabType }) {
  const config = TAB_CONFIG[tabType]
  const dispatch = useDispatch()

  const { closeModal, modalOpen, addMode, tabSelections, updateTabSelection } = useContext(EducationalStructureContext)
  const did = addMode ? 0 : (modalOpen?.did || 0)

  const state = useSelector(state => state.educational_structure)
  const groups = state[config.groupsKey]
  const courses = config.coursesKey ? state[config.coursesKey] : null
  const { tabLoading } = state

  // Несохранённые отметки живут в контексте модалки (не в локальном state),
  // чтобы переключение вкладок без сохранения их не сбрасывало
  const selection = tabSelections[tabType] || {}
  const checkedGroups = selection.groups || new Set()
  const checkedCourses = selection.courses || new Set()
  const withClosed = selection.withClosed || false
  const [saving, setSaving] = useState(false)

  // Загрузить данные при открытии
  useEffect(() => {
    dispatch(config.getGroups(did))
    if (config.getCourses) {
      dispatch(config.getCourses(did, withClosed))
    }
  }, [did])

  // Перезагрузить предметы при смене "С закрытыми"
  useEffect(() => {
    if (config.hasClosed && config.getCourses) {
      dispatch(config.getCourses(did, withClosed))
    }
  }, [withClosed])

  // Инициализировать чекбоксы из загруженных данных (только один раз за сессию открытия модалки)
  useEffect(() => {
    if (groups && selection.groups === undefined) {
      updateTabSelection(tabType, { groups: initChecked(groups) })
    }
  }, [groups])

  useEffect(() => {
    if (courses && selection.courses === undefined) {
      updateTabSelection(tabType, { courses: initChecked(courses) })
    }
  }, [courses])

  const toggleGroup = (id) => {
    const next = new Set(checkedGroups)
    next.has(id) ? next.delete(id) : next.add(id)
    updateTabSelection(tabType, { groups: next })
  }

  const toggleCourse = (id) => {
    const next = new Set(checkedCourses)
    next.has(id) ? next.delete(id) : next.add(id)
    updateTabSelection(tabType, { courses: next })
  }

  const selectAllGroups = (val) => {
    updateTabSelection(tabType, { groups: val ? new Set((groups || []).map(getItemId)) : new Set() })
  }

  const selectAllCourses = (val) => {
    updateTabSelection(tabType, { courses: val ? new Set((courses || []).map(getItemId)) : new Set() })
  }

  const setWithClosed = (val) => updateTabSelection(tabType, { withClosed: val })

  const handleAutoCheck = useCallback(async () => {
    if (!config.autoCheck) return
    const gids = [...checkedGroups]
    if (!gids.length) return
    const result = await dispatch(config.autoCheck(did, gids))
    if (result?.cids) {
      const next = new Set(checkedCourses)
      result.cids.forEach(cid => next.add(cid))
      updateTabSelection(tabType, { courses: next })
    }
  }, [checkedGroups, checkedCourses, config, did, dispatch, tabType, updateTabSelection])

  const handleSave = async () => {
    setSaving(true)
    let ok = false
    try {
      const gids = [...checkedGroups]
      if (config.singleList) {
        // Для элективов: передаём все возможные elective gid-ы чтобы action мог
        // сохранить объединённый список (основные классы + выбранные элективы)
        const allElectiveGids = (groups || []).map(getItemId).filter(Boolean)
        ok = await dispatch(config.saveLinks(did, gids, allElectiveGids))
      } else {
        const cids = [...checkedCourses]
        ok = await dispatch(config.saveLinks(did, gids, cids))
      }
    } finally {
      setSaving(false)
      if (ok) closeModal()
    }
  }

  const coursesFooter = (
    <>
      {config.hasClosed && (
        <label className={styles.checkItem}>
          <input
            type='checkbox'
            checked={withClosed}
            onChange={(e) => setWithClosed(e.target.checked)}
          />
          <span>С закрытыми</span>
        </label>
      )}
      {config.hasAutoCheck && (
        <button
          type='button'
          className={styles.autoCheckBtn}
          onClick={handleAutoCheck}
          disabled={checkedGroups.size === 0 || tabLoading}
        >
          {config.autoCheckLabel}
        </button>
      )}
    </>
  )

  return (
    <>
      <div className={styles.modalMain}>
        <div className={`${styles.tabWrapper} ${styles.curateWrapper}`}>
          {tabLoading && <div className={styles.loadingOverlay}>Загрузка...</div>}

          <CheckList
            title={config.groupsTitle}
            items={groups}
            checked={checkedGroups}
            onChange={toggleGroup}
            onSelectAll={selectAllGroups}
            emptyHint={config.singleList
              ? 'Список пуст: элективная группа появится здесь, только если её предмет отмечен как элективный на вкладке «Основное образование» этого узла.'
              : undefined}
          />

          {!config.singleList && (
            <CheckList
              title={config.coursesTitle}
              items={courses}
              checked={checkedCourses}
              onChange={toggleCourse}
              onSelectAll={selectAllCourses}
              footer={coursesFooter}
            />
          )}
        </div>
      </div>
      <ModalFooter handleSubmit={handleSave} closeModal={closeModal} loading={saving} />
    </>
  )
}
